import type { Locale } from '../app/locale/resolveLocale';
import type { PersonalityType } from '../data/taxonomy';
import type { ScoreEventInput } from './scoring';

type PrimaryKind = 'meme' | 'escape' | 'confront' | 'repair' | 'cool' | 'hold' | 'heat';
type FollowUpKind = 'repair' | 'boundary' | 'direct' | 'delay' | 'control' | 'private' | 'chaos' | 'meme' | 'replay';
type TriggerKind = 'boundary' | 'readReceipt' | 'groupHeat' | 'awkward' | 'publicPressure' | 'relationship';

type EvidenceSlot<TKind extends string> = {
  kind: TKind;
  label: string;
  text: string;
};

export type ResultEvidence = {
  heading: string;
  primary: EvidenceSlot<PrimaryKind>;
  followUp: EvidenceSlot<FollowUpKind>;
  triggers: {
    label: string;
    kind: TriggerKind[];
    items: string[];
    text: string;
  };
  risk: EvidenceSlot<FollowUpKind | PrimaryKind>;
  repair: EvidenceSlot<FollowUpKind | PrimaryKind>;
};

const primaryLabels: Record<Locale, Record<PrimaryKind, string>> = {
  'zh-CN': {
    meme: '第一反应',
    escape: '第一反应',
    confront: '第一反应',
    repair: '第一反应',
    cool: '第一反应',
    hold: '第一反应',
    heat: '第一反应'
  },
  en: {
    meme: 'First reaction',
    escape: 'First reaction',
    confront: 'First reaction',
    repair: 'First reaction',
    cool: 'First reaction',
    hold: 'First reaction',
    heat: 'First reaction'
  }
};

const followUpLabels: Record<Locale, Record<FollowUpKind, string>> = {
  'zh-CN': {
    repair: '后续处理',
    boundary: '后续处理',
    direct: '后续处理',
    delay: '后续处理',
    control: '后续处理',
    private: '后续处理',
    chaos: '后续处理',
    meme: '后续处理',
    replay: '后续处理'
  },
  en: {
    repair: 'Follow-up',
    boundary: 'Follow-up',
    direct: 'Follow-up',
    delay: 'Follow-up',
    control: 'Follow-up',
    private: 'Follow-up',
    chaos: 'Follow-up',
    meme: 'Follow-up',
    replay: 'Follow-up'
  }
};

const triggerLabels: Record<Locale, Record<TriggerKind, string>> = {
  'zh-CN': {
    boundary: '边界被踩',
    readReceipt: '已读不回',
    groupHeat: '群聊升温',
    awkward: '冷场尴尬',
    publicPressure: '公开压力',
    relationship: '关系卡顿'
  },
  en: {
    boundary: 'crossed lines',
    readReceipt: 'left on read',
    groupHeat: 'group heat',
    awkward: 'awkward silence',
    publicPressure: 'public pressure',
    relationship: 'relationship friction'
  }
};

const primaryCopy: Record<Locale, Record<PrimaryKind, string[]>> = {
  'zh-CN': {
    meme: ['先用梗稳住气氛', '先丢个梗让场面别掉地上', '把冲突先包进玩笑里'],
    escape: ['先把自己从现场撤出来', '压力一上来先拉开距离', '先离开高压对话区'],
    confront: ['先把问题点出来', '不舒服时会直接开口', '遇到不公会先说清楚'],
    repair: ['先找办法把局面接住', '第一反应是把事情处理掉', '会先给关系找一个出口'],
    cool: ['先维持表面体面', '先把场面稳住不露太多', '会先装作还能接住'],
    hold: ['先压住反应观察一下', '会先忍一拍再决定', '第一步通常是把情绪收住'],
    heat: ['压力一上来反应会先冲出去', '情绪被点燃时很难装没事', '会先被现场气氛推着走']
  },
  en: {
    meme: ['you soften the room with a joke first', 'you let humor take the first hit', 'you wrap tension in a joke before answering'],
    escape: ['you create distance before responding', 'you step out of the pressure first', 'you leave the hot zone before deciding'],
    confront: ['you name the problem early', 'you speak up when something feels off', 'you call out the issue before it drifts'],
    repair: ['you look for a way to stabilize the moment', 'you move toward a fix first', 'you try to give the situation an exit'],
    cool: ['you keep the surface composed first', 'you steady the room before showing much', 'you act fine while reading the room'],
    hold: ['you hold the first reaction and observe', 'you pause before choosing a lane', 'you contain the feeling before moving'],
    heat: ['pressure pushes your reaction out fast', 'you find it hard to play fine once the room spikes', 'the moment can pull your emotions forward']
  }
};

const followUpCopy: Record<Locale, Record<FollowUpKind, string[]>> = {
  'zh-CN': {
    repair: ['再补一句认真话', '后面会把真实态度说清', '会找机会补一句准话'],
    boundary: ['会把底线落到一句具体的话', '后续更倾向把边界说出来', '会把不舒服翻译成规则'],
    direct: ['会把问题讲明白，不让它继续糊着', '后续会直接点出核心', '会把模糊地带说清楚'],
    delay: ['认真回应会延迟一拍', '会先缓一缓再决定要不要接住', '会把这件事先放一边处理'],
    control: ['会用体面话术把场面控住', '后续更重视别让气氛失控', '会先把表达包得更好看'],
    private: ['会先转到私下消化和复盘', '会把现场情绪先拿去旁路处理', '后续更像私下整理证据和感受'],
    chaos: ['容易顺手打开剧情支线', '会把场面推向更有戏的一边', '后续可能给剧情加一笔'],
    meme: ['会继续用玩笑接住尴尬', '后续还是偏向用梗缓冲', '会让表情包继续扛一会儿'],
    replay: ['会在脑内多走几遍草稿', '后续容易反复加工刚才那一幕', '会先复盘到自己觉得安全']
  },
  en: {
    repair: ['then you clarify the real point', 'then you come back with the serious line', 'then you try to land the honest answer'],
    boundary: ['then you turn discomfort into a clear line', 'then you put the boundary into words', 'then you make the limit concrete'],
    direct: ['then you say the core issue plainly', 'then you pull the vague part into the open', 'then you make the point hard to miss'],
    delay: ['the serious answer arrives a beat late', 'you pause before deciding whether to answer', 'you set the thread aside before handling it'],
    control: ['then you package the reply so the room stays stable', 'then you keep the vibe from spilling over', 'then you make the response socially cleaner'],
    private: ['then you process it privately', 'then you move the feeling into the side channel', 'then you organize receipts and feelings offstage'],
    chaos: ['then a side plot starts looking tempting', 'then you may add one twist to the scene', 'then the story branch starts glowing'],
    meme: ['then humor keeps carrying the awkwardness', 'then the joke keeps the moment afloat', 'then memes keep taking the pressure'],
    replay: ['then you replay the draft in your head', 'then the moment gets processed again', 'then you keep editing the response internally']
  }
};

const riskCopy: Record<Locale, Record<PrimaryKind, string[]>> = {
  'zh-CN': {
    meme: ['别人可能以为你在糊弄', '认真话容易被梗挡在后面', '你越想缓和，越可能显得不够明确'],
    escape: ['对方可能只看到你消失', '拉开距离容易被误会成断联', '你需要空间，但别人未必看得懂'],
    confront: ['话太快时容易带热度', '问题说清了，温度也可能一起上来', '直接表达有时会显得太硬'],
    repair: ['太快修复会压过自己的感受', '你可能把责任接得太早', '一直补救会让自己变累'],
    cool: ['太会稳住场面会显得难读', '别人可能看不出你真实不舒服', '体面久了容易把真话藏住'],
    hold: ['忍太久会让情绪延迟爆出', '别人可能以为你真的没事', '停太久会错过表达时机'],
    heat: ['情绪先到时容易让重点跑偏', '当下很真，但也可能太满', '上头会让后续补救成本变高']
  },
  en: {
    meme: ['people may read the joke as dodging', 'the real point can hide behind the bit', 'softening the room can make you look unclear'],
    escape: ['people may only see you disappear', 'taking space can look like disconnecting', 'your need for room may not be obvious'],
    confront: ['the heat can travel with the truth', 'clarity can land harder than intended', 'directness can read too sharp'],
    repair: ['fixing fast can outrun your own feelings', 'you may take responsibility too early', 'constant repair can become tiring'],
    cool: ['being composed can make you hard to read', 'people may miss that you were uncomfortable', 'too much polish can hide the real answer'],
    hold: ['holding it too long can delay the explosion', 'people may assume you are actually fine', 'the pause can cost you the timing'],
    heat: ['the feeling can pull focus from the point', 'the reaction is real but may feel too full', 'running hot raises the repair cost']
  }
};

const repairCopy: Record<Locale, Record<FollowUpKind, string[]>> = {
  'zh-CN': {
    repair: ['缓冲可以先来，但后面补一句真话', '玩笑或台阶之后，把真实态度落地', '先稳住没问题，记得补上认真回应'],
    boundary: ['把“不舒服”说成一句具体边界', '少解释一点，多给一句清楚规则', '把底线讲短，关系反而更好接住'],
    direct: ['先说重点，再补一点温度', '直球之后加一句关系还在', '把话说清，也给对方一个台阶'],
    delay: ['缓一缓可以，但别让回应消失', '撤开之后，给一个回来的时间点', '先放一边可以，记得回来收口'],
    control: ['体面控场后，补一句真实感受', '别只让话术漂亮，也让态度可见', '稳住场面之后，把重点落下来'],
    private: ['私下复盘后，挑一句能发出去的真话', '证据可以留，最后还是要回到表达', '旁路处理完，给关系一个明确回声'],
    chaos: ['支线可以看，但别忘了把主线拉回来', '加戏之前先问自己要不要承担后果', '剧情打开后，记得留一个刹车键'],
    meme: ['梗可以继续，但别让真话永远排队', '玩笑够了之后，补一个明确态度', '让表情包暖场，别让它替你做全部回应'],
    replay: ['脑内改稿可以，但最后只发一句准的', '复盘到一半就够了，选一句能落地的', '别把所有情绪都留给半夜处理']
  },
  en: {
    repair: ['the buffer can come first, then land the honest line', 'after the joke or ramp, make your real stance visible', 'steady the room, then answer seriously'],
    boundary: ['turn “this feels off” into one concrete boundary', 'explain less and state the line more clearly', 'a shorter boundary can be easier to respect'],
    direct: ['say the point, then add warmth', 'after the direct line, signal the relationship is still there', 'make it clear and leave a ramp'],
    delay: ['taking space is fine, but do not let the answer vanish', 'step away with a return point', 'set it aside, then close the loop'],
    control: ['after the polished reply, add the real feeling', 'make the stance visible, not just the wording', 'steady the room, then land the point'],
    private: ['after private processing, choose one sendable truth', 'receipts can help, but the relationship still needs a signal', 'bring one clear echo back from the side channel'],
    chaos: ['the side plot can wait; bring the main thread back', 'before adding drama, check the cost', 'if the story branches, keep a brake nearby'],
    meme: ['memes can keep helping, but the real answer still needs a turn', 'after the bit, add one clear stance', 'let the joke warm the room, not replace the reply'],
    replay: ['edit internally, then send one clean line', 'stop the replay halfway and choose one usable response', 'do not leave all the feeling for midnight']
  }
};

function increment<T extends string>(counts: Map<T, number>, key: T) {
  counts.set(key, (counts.get(key) ?? 0) + 1);
}

function primaryKindFor(event: ScoreEventInput): PrimaryKind {
  if (event.coreEvent === 'double_tap_meme') return 'meme';
  if (event.coreEvent === 'swipe_up_escape' || event.coreEvent === 'fast_fling_nope') return 'escape';
  if (event.coreEvent === 'swipe_down_confront') return 'confront';
  if (event.coreEvent === 'swipe_right_fix') return 'repair';
  if (event.coreEvent === 'swipe_left_play_cool') return 'cool';
  if (event.coreEvent === 'shake_spiral') return 'heat';
  return 'hold';
}

function followUpKindFor(choiceId?: string): FollowUpKind {
  if (choiceId === 'offer_solution' || choiceId === 'cancel_cleanly' || choiceId === 'organize_group') return 'repair';
  if (choiceId === 'set_boundary' || choiceId === 'hard_exit') return 'boundary';
  if (choiceId === 'direct_callout') return 'direct';
  if (choiceId === 'leave_on_read' || choiceId === 'fake_busy' || choiceId === 'mute_chat' || choiceId === 'ghost_then_return') return 'delay';
  if (choiceId === 'polite_venom') return 'control';
  if (choiceId === 'screenshot_to_friend') return 'private';
  if (choiceId === 'plot_twist') return 'chaos';
  if (choiceId === 'meme_reply') return 'meme';
  return 'replay';
}

function triggerKindFor(event: ScoreEventInput): TriggerKind {
  if (event.sceneId.includes('read_no_reply') || event.sceneId.includes('flat_reply') || event.sceneId.includes('voice_message')) {
    return 'readReceipt';
  }
  if (event.category === 'group' || event.sceneId.includes('group') || event.sceneId.includes('meeting')) {
    return 'groupHeat';
  }
  if (event.category === 'boundary' || event.sceneId.includes('boundary') || event.sceneId.includes('queue') || event.sceneId.includes('bill')) {
    return 'boundary';
  }
  if (event.category === 'meme' || event.sceneId.includes('comment') || event.sceneId.includes('photo')) {
    return 'awkward';
  }
  if (event.category === 'pressure') {
    return 'publicPressure';
  }
  return 'relationship';
}

function strongest<T extends string>(counts: Map<T, number>, fallback: T): T {
  let best = fallback;
  let bestCount = Number.NEGATIVE_INFINITY;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key;
      bestCount = count;
    }
  }
  return best;
}

function resolveFollowUpKind(primaryKind: PrimaryKind, counts: Map<FollowUpKind, number>): FollowUpKind {
  const repairLike = (counts.get('repair') ?? 0) + (counts.get('boundary') ?? 0) + (counts.get('direct') ?? 0);
  if (primaryKind === 'meme' && repairLike >= 2) return 'repair';
  return strongest(counts, 'delay');
}

function eventSignature(events: ScoreEventInput[]) {
  return events
    .map((event) => `${event.sceneId}:${event.coreEvent}:${event.secondaryChoiceId ?? 'none'}`)
    .join('|');
}

function hashSeed(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function chooseVariant(variants: string[], seed: string) {
  return variants[hashSeed(seed) % variants.length];
}

function topTriggers(events: ScoreEventInput[], locale: Locale): { kinds: TriggerKind[]; items: string[] } {
  const counts = new Map<TriggerKind, number>();
  const firstSeen = new Map<TriggerKind, number>();
  events.forEach((event, index) => {
    const kind = triggerKindFor(event);
    increment(counts, kind);
    if (!firstSeen.has(kind)) firstSeen.set(kind, index);
  });

  const kinds = [...counts.keys()]
    .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0))
    .slice(0, 3);

  if (kinds.length === 0) {
    kinds.push('relationship');
  }

  return {
    kinds,
    items: kinds.map((kind) => triggerLabels[locale][kind])
  };
}

export function resultEvidence(type: PersonalityType, locale: Locale, events: ScoreEventInput[] = []): ResultEvidence {
  const signature = eventSignature(events);
  const primaryCounts = new Map<PrimaryKind, number>();
  const followUpCounts = new Map<FollowUpKind, number>();

  for (const event of events) {
    increment(primaryCounts, primaryKindFor(event));
    increment(followUpCounts, followUpKindFor(event.secondaryChoiceId));
  }

  const primaryKind = strongest(primaryCounts, 'meme');
  const followUpKind = resolveFollowUpKind(primaryKind, followUpCounts);
  const triggerSummary = topTriggers(events, locale);
  const separator = locale === 'zh-CN' ? ' / ' : ' / ';
  const seedBase = `${type.typeId}:${locale}:${signature}`;

  return {
    heading: locale === 'zh-CN' ? '系统抓到的反应轨迹' : 'Reaction trail',
    primary: {
      kind: primaryKind,
      label: primaryLabels[locale][primaryKind],
      text: chooseVariant(primaryCopy[locale][primaryKind], `${seedBase}:primary`)
    },
    followUp: {
      kind: followUpKind,
      label: followUpLabels[locale][followUpKind],
      text: chooseVariant(followUpCopy[locale][followUpKind], `${seedBase}:followUp`)
    },
    triggers: {
      label: locale === 'zh-CN' ? '触发场景' : 'Trigger scenes',
      kind: triggerSummary.kinds,
      items: triggerSummary.items,
      text: triggerSummary.items.join(separator)
    },
    risk: {
      kind: primaryKind,
      label: locale === 'zh-CN' ? '容易翻车' : 'Risk',
      text: chooseVariant(riskCopy[locale][primaryKind], `${seedBase}:risk`)
    },
    repair: {
      kind: followUpKind,
      label: locale === 'zh-CN' ? '补救一句' : 'Repair line',
      text: chooseVariant(repairCopy[locale][followUpKind], `${seedBase}:repair`)
    }
  };
}
